import { FC, useRef, useState } from "react";
import { AsyncSelect, FormField, TableCell, TableHeadCell } from "@rothoblaas/design-system";
import AdminCrudTable from "../../../../components/ui/AdminCrudTable";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ScrewDiameter } from "../../../../http/screwConnector/types/screwDiameters";
import { ScrewFamily } from "../../../../http/screwConnector/types/screwFamilies";
import useScrewConnectorScrewDiametersApi from "../../../../http/screwConnector/useAdminScrewDiameters";
import useScrewConnectorScrewFamiliesApi from "../../../../http/screwConnector/useAdminScrewFamilies";
import ScrewDiameterForm from "./ScrewDiameterForm";
import styles from "./AdminScrewDiameters.module.css";

const AdminScrewDiameters: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const screwDiametersApi = useScrewConnectorScrewDiametersApi();
  const screwFamiliesApi = useScrewConnectorScrewFamiliesApi();
  const screwFamilyOptions = useAsyncOptions(screwFamiliesApi.getScrewFamilies);
  const [selectedFamily, setSelectedFamily] = useState<ScrewFamily>();
  const screwFamiliesAborter = useRef(new AbortController());
  // A generous limit, so the rows resolve their family name without a per-row lookup.
  const { data: screwFamilies } = screwFamiliesApi.useScrewFamilies(
    { limit: 200 },
    screwFamiliesAborter.current.signal,
  );

  const screwFamilyName = (id: string) =>
    screwFamilies?.items.find((family) => family.id === id)?.name ??
    (id === selectedFamily?.id ? selectedFamily.name : id);

  return (
    // the family filter above the shared table, which has the search only
    <div className={styles.root}>
      <div className={styles.filter}>
        <FormField label={t.screwDiameters.filterByFamily} isOptional>
          {({ labelId }) => (
            <AsyncSelect<ScrewFamily, false>
              {...screwFamilyOptions}
              inputId={labelId}
              value={selectedFamily ?? null}
              isClearable
              isMulti={false}
              placeholder={`${i18n.translation.common.select}...`}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              onChange={(option) => setSelectedFamily(option ?? undefined)}
            />
          )}
        </FormField>
      </div>
      <AdminCrudTable<ScrewDiameter>
        filterPrefix="screw_diameters"
        useList={(query, signal) => screwDiametersApi.useScrewDiameters(query, signal, selectedFamily?.id)}
        head={
          <>
            <TableHeadCell align="left">{t.screwDiameters.fields.screwFamily}</TableHeadCell>
            <TableHeadCell align="left">{t.screwDiameters.fields.diameter}</TableHeadCell>
            <TableHeadCell align="left">{t.screwDiameters.fields.coreDiameter}</TableHeadCell>
            <TableHeadCell align="left">{t.screwDiameters.fields.headDiameter}</TableHeadCell>
            <TableHeadCell align="left">{t.screwDiameters.fields.yieldMoment}</TableHeadCell>
            <TableHeadCell align="left">{t.screwDiameters.fields.tensileCapacity}</TableHeadCell>
          </>
        }
        renderRow={(item) => (
          <>
            <TableCell>{screwFamilyName(item.screwFamilyId)}</TableCell>
            <TableCell>
              {item.diameter} {i18n.translation.common.unitMm}
            </TableCell>
            <TableCell>
              {item.coreDiameter} {i18n.translation.common.unitMm}
            </TableCell>
            <TableCell>
              {item.headDiameter} {i18n.translation.common.unitMm}
            </TableCell>
            <TableCell>
              {item.yieldMoment} {i18n.translation.common.unitNMm}
            </TableCell>
            <TableCell>
              {item.tensileCapacity} {i18n.translation.common.unitN}
            </TableCell>
          </>
        )}
        onDelete={(item) => screwDiametersApi.deleteScrewDiameter(item.id)}
        deleteTitle={t.screwDiameters.singular}
        canAdd
        // a new diameter starts from the family the list is filtered by
        renderForm={(item, onClose) => (
          <ScrewDiameterForm
            screwDiameter={item}
            defaultScrewFamily={item ? undefined : selectedFamily}
            onClose={onClose}
          />
        )}
      />
    </div>
  );
};

export default AdminScrewDiameters;
